import { createReducer, createAction } from '@reduxjs/toolkit';
import NetInfo from '@react-native-community/netinfo';
import DeviceInfo from 'react-native-device-info';
import { Linking, Platform } from 'react-native';
import i18n from 'i18n-js';

import api from '@api';
import { isWeb, Alert, isAndroid, getSecureValue, setSecureValue } from '@helpers';
import { restoreCall } from '@reducers/utils';
import { sendLog } from '@reducers/dataDogs';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';
import { CHECK_API_CACHE } from '@v2/pages/common/constants/base.constants';
import type { AppThunk, RootState } from '@store/types';
import type { AppDispatch } from '@reducers/utils/types';
import type { NotificationState, UpdateNotificationStatusPayload, PlatformVersions, CachedPlatformVersions } from './types';
import { NotificationStatusName } from './types';
import type { CheckApiResponse } from '@api/domain/types';

// Re-export types for consumers
export type { NotificationState, UpdateNotificationStatusPayload } from './types';
export { NotificationStatusName } from './types';
export type { CheckApiResponse } from '../../api/domain/types';

// === Initial State ===

const initState: NotificationState = {
  [NotificationStatusName.NeedUpdateApp]: false,
  [NotificationStatusName.WarnToUpdateApp]: false,
  [NotificationStatusName.IsServerWorking]: true,
  [NotificationStatusName.IsInternetReachable]: true,
  [NotificationStatusName.Validating]: false,
  ipAddress: null,
  versions: {},
};

// === Selectors ===

export const selectNeedUpdateApp = (state: RootState) => state.notification[NotificationStatusName.NeedUpdateApp];
export const selectWarnToUpdateApp = (state: RootState) => state.notification[NotificationStatusName.WarnToUpdateApp];
export const selectIsServerWorking = (state: RootState) => state.notification[NotificationStatusName.IsServerWorking];
export const selectIsInternetReachable = (state: RootState) => state.notification[NotificationStatusName.IsInternetReachable];
export const selectValidating = (state: RootState) => state.notification[NotificationStatusName.Validating];
export const selectIpAddress = (state: RootState) => state.notification.ipAddress;
export const selectVersions = (state: RootState) => state.notification.versions;

// === Version comparison ===

// Returns -1 if a < b, 0 if equal, 1 if a > b. Tolerates missing parts (treated as 0).
const compareVersions = (a: string, b: string): number => {
  const aParts = a.split('.').map((p) => parseInt(p, 10) || 0);
  const bParts = b.split('.').map((p) => parseInt(p, 10) || 0);
  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i++) {
    const av = aParts[i] ?? 0;
    const bv = bParts[i] ?? 0;
    if (av < bv) return -1;
    if (av > bv) return 1;
  }
  return 0;
};

// === Polyfill ===

AbortSignal.timeout = function timeout(ms: number): AbortSignal {
  const ctrl = new AbortController();
  setTimeout(() => ctrl.abort(), ms);
  return ctrl.signal;
};

// === Helpers ===

export const fetchAPI = async (): Promise<Response> => {
  const { baseURL } = await api();
  return fetch(`${baseURL}/v2/membership/checkApi`, { method: 'POST', signal: AbortSignal.timeout(10000) });
};

// === Actions ===

export const updateNotificationStatus = createAction<UpdateNotificationStatusPayload>('notification/updateNotificationStatus');
export const finishValidating = createAction('notification/finishValidating');
export const setSkipScreen = createAction('notification/setSkipScreen');
export const saveUserIP = createAction<string>('notification/saveUserIP');
export const setVersions = createAction<CheckApiResponse>('notification/setVersions');
export const mergeVersions = createAction<Partial<CheckApiResponse>>('notification/mergeVersions');
export const clear = createAction('clear');

// === Platform version cache ===

const readPlatformVersionCache = (): PlatformVersions | null => {
  const raw = getSecureValue(CHECK_API_CACHE.KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw as string) as CachedPlatformVersions;
    if (Date.now() > parsed.expiresAt) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writePlatformVersionCache = (result: CheckApiResponse): void => {
  const payload: CachedPlatformVersions = {
    minimumVersionAppAndroid: result.minimumVersionAppAndroid,
    minimumVersionAppIOS: result.minimumVersionAppIOS,
    previousVersionAppAndroid: result.previousVersionAppAndroid,
    previousVersionAppIOS: result.previousVersionAppIOS,
    expiresAt: Date.now() + CHECK_API_CACHE.TTL_MS,
  };
  setSecureValue(CHECK_API_CACHE.KEY, JSON.stringify(payload));
};

const applyPlatformVersionGate = (dispatch: AppDispatch, versions: PlatformVersions): void => {
  if (__DEV__) {
    return;
  }
  const minimumVersion = isAndroid ? versions.minimumVersionAppAndroid : versions.minimumVersionAppIOS;
  const previousVersion = isAndroid ? versions.previousVersionAppAndroid : versions.previousVersionAppIOS;
  const currentVersion = DeviceInfo.getVersion();

  //TODO: uncomment this variant when app will be ready to display blocking screen and remove next chunk of code
  // const needUpdateApp = !!currentVersion && !!minimumVersion && compareVersions(currentVersion, minimumVersion) < 0;
  // const warnToUpdateApp = !needUpdateApp && !!currentVersion && !!previousVersion && compareVersions(currentVersion, previousVersion) < 0;

  // dispatch(updateNotificationStatus({ name: NotificationStatusName.NeedUpdateApp, value: needUpdateApp }));
  // dispatch(updateNotificationStatus({ name: NotificationStatusName.WarnToUpdateApp, value: warnToUpdateApp }));

  
  // TEMP: never block — surface both "must update" and "should update" as the non-blocking warning instead.
  const needUpdateApp = !!currentVersion && !!minimumVersion && compareVersions(currentVersion, minimumVersion) < 0;
  const warnToUpdateApp = !!currentVersion && !!previousVersion && compareVersions(currentVersion, previousVersion) < 0;

  dispatch(updateNotificationStatus({ name: NotificationStatusName.NeedUpdateApp, value: false }));
  dispatch(updateNotificationStatus({ name: NotificationStatusName.WarnToUpdateApp, value: needUpdateApp || warnToUpdateApp }));
};

// === Thunks ===

export const skipScreen = (): AppThunk => (dispatch) => {
  dispatch(setSkipScreen());
};

const buildUpdateAppLogData = (state: RootState) => {
  const { versions } = state.notification;
  return {
    platform: Platform.OS,
    currentVersion: DeviceInfo.getVersion(),
    minimumVersion: isAndroid ? versions.minimumVersionAppAndroid : versions.minimumVersionAppIOS,
    previousVersion: isAndroid ? versions.previousVersionAppAndroid : versions.previousVersionAppIOS,
  };
};

export const logNeedUpdateApp = (): AppThunk => (dispatch, getState) => {
  dispatch(sendLog({ title: LOGGER_TITLES.APP_VERSION_UNSUPPORTED, data: buildUpdateAppLogData(getState()), status: 'warn' }));
};

export const logWarnToUpdateApp = (): AppThunk => (dispatch, getState) => {
  dispatch(sendLog({ title: LOGGER_TITLES.APP_VERSION_OUTDATED, data: buildUpdateAppLogData(getState()), status: 'warn' }));
};

export const checkStatusAgain = (): AppThunk => (dispatch) => {
  NetInfo.fetch().then((state) => validateState({ state, dispatch }));
};

export const serverNoAvailable = (): AppThunk => (dispatch) => {
  try {
    fetchAPI()
      .then((resut) => {
        return resut.json();
      })
      .then((result: CheckApiResponse) => {
        if (result.isError) {
          throw 'error';
        }
        dispatch(saveUserIP(result.ipAddress));
        dispatch(updateNotificationStatus({ name: NotificationStatusName.IsServerWorking, value: true }));
      })
      .catch(() => {
        dispatch(updateNotificationStatus({ name: NotificationStatusName.IsServerWorking, value: false }));
      });
  } catch (e) {
    dispatch(updateNotificationStatus({ name: NotificationStatusName.IsServerWorking, value: false }));
  }
};

const backendRequest = async (dispatch: AppDispatch, restore = false): Promise<void> => {
  try {
    fetchAPI()
      .then((response) => response.json())
      .then((result: CheckApiResponse) => {
        if (result.isError) {
          throw 'error';
        }
        dispatch(saveUserIP(result.ipAddress));
        dispatch(updateNotificationStatus({ name: NotificationStatusName.IsServerWorking, value: true }));
        if (restore) {
          dispatch(restoreCall());
        }

        dispatch(setVersions(result));

        if (isWeb) {
          dispatch(updateNotificationStatus({ name: NotificationStatusName.NeedUpdateApp, value: false }));
          dispatch(updateNotificationStatus({ name: NotificationStatusName.WarnToUpdateApp, value: false }));
          return;
        }

        writePlatformVersionCache(result);
        applyPlatformVersionGate(dispatch, result);
      })
      .catch(async () => {
        const net = await NetInfo.refresh();
        if (net.isInternetReachable === false || net.type === 'none') {
          dispatch(updateNotificationStatus({ name: NotificationStatusName.IsInternetReachable, value: false }));
          return;
        }
        dispatch(updateNotificationStatus({ name: NotificationStatusName.IsServerWorking, value: false }));
      });
  } catch (e) {
    dispatch(updateNotificationStatus({ name: NotificationStatusName.IsServerWorking, value: false }));
  }
};

interface ValidateStateParams {
  state: { isInternetReachable: boolean | null; type?: string };
  dispatch: AppDispatch;
}

const validateState = async ({ state, dispatch }: ValidateStateParams): Promise<void> => {
  try {
    dispatch(updateNotificationStatus({ name: NotificationStatusName.Validating, value: true }));
    dispatch(updateNotificationStatus({ name: NotificationStatusName.IsInternetReachable, value: state.isInternetReachable as boolean }));
    if (state.isInternetReachable) {
      await backendRequest(dispatch, true);
    }
  } finally {
    dispatch(finishValidating());
  }
};

export const validateRequestError = (): AppThunk => (dispatch, getState) => {
  let alertShown = false;

  const showNetworkAlert = (): void => {
    if (!alertShown) {
      alertShown = true;
      Alert({
        title: i18n.t('No network'),
        text: i18n.t('Please check your internet connection and try again later.'),
      });
    }
  };

  NetInfo.fetch().then((state) => {
    try {
      if (state.type === 'none') {
        fetchAPI().catch(() => {
          return noMobileDataAlert(dispatch);
        });
      }
      if (state.isInternetReachable === null) {
        return setTimeout(() => {
          dispatch(validateRequestError());
        }, 2000);
      }
      const {
        notification: { isInternetReachable },
      } = getState();
      dispatch(updateNotificationStatus({ name: NotificationStatusName.Validating, value: true }));
      if (state.isInternetReachable) {
        fetchAPI()
          .then((resut) => {
            return resut.json();
          })
          .then((result: CheckApiResponse) => {
            if (result.isError) {
              throw 'error';
            }
            dispatch(saveUserIP(result.ipAddress));
            dispatch(updateNotificationStatus({ name: NotificationStatusName.IsServerWorking, value: true }));
          })
          .catch(() => {
            dispatch(updateNotificationStatus({ name: NotificationStatusName.IsServerWorking, value: false }));
          });
      } else {
        if (!isInternetReachable) {
          return;
        }

        fetchAPI().catch(() => {
          showNetworkAlert();
          dispatch(updateNotificationStatus({ name: NotificationStatusName.IsInternetReachable, value: false }));
        });
      }
    } finally {
      dispatch(finishValidating());
    }
  });
};

const validateInitialState = async ({ state, dispatch }: ValidateStateParams): Promise<void> => {
  try {
    dispatch(updateNotificationStatus({ name: NotificationStatusName.Validating, value: true }));
    if (state.type === 'none') {
      return noMobileDataAlert(dispatch);
    }
    dispatch(
      updateNotificationStatus({
        name: NotificationStatusName.IsInternetReachable,
        value: state.isInternetReachable !== false,
      })
    );
  } finally {
    dispatch(finishValidating());
  }
};

let isMobileDataAlert = false;
const noMobileDataAlert = (dispatch: AppDispatch): void => {
  if (isMobileDataAlert) {
    return;
  }
  isMobileDataAlert = true;
  Alert({
    title: i18n.t('No Internet Connection'),
    text: i18n.t('Turn on mobile data or use Wi-Fi to access data.'),
    actions: [
      {
        text: i18n.t('Settings'),
        onPress: () => {
          isMobileDataAlert = false;
          Linking.openURL('App-Prefs:Network');
          dispatch(restoreCall());
        },
        style: 'primary',
      },
      {
        text: i18n.t('OK'),
        onPress: () => {
          isMobileDataAlert = false;
          dispatch(restoreCall());
        },
      },
    ],
  });
};

export const initNotification = (): AppThunk => async (dispatch) => {
  if (!isWeb) {
    const cached = readPlatformVersionCache();
    if (cached) {
      dispatch(
        mergeVersions({
          minimumVersionAppAndroid: cached.minimumVersionAppAndroid,
          minimumVersionAppIOS: cached.minimumVersionAppIOS,
          previousVersionAppAndroid: cached.previousVersionAppAndroid,
          previousVersionAppIOS: cached.previousVersionAppIOS,
        })
      );
      applyPlatformVersionGate(dispatch, cached);
    }
  }

  backendRequest(dispatch);

  NetInfo.fetch()
    .then((state) => validateInitialState({ state, dispatch }))
    .catch((e) => {
      console.error('Error happened while initNotification', e);
    });
};

// === Reducer ===

export default createReducer<NotificationState>(initState, (builder) => {
  builder
    .addCase(setSkipScreen, () => ({
      ...initState,
    }))
    .addCase(updateNotificationStatus, (state, action) => ({
      ...state,
      [action.payload.name]: action.payload.value,
    }))
    .addCase(finishValidating, (state) => ({
      ...state,
      validating: false,
    }))
    .addCase(saveUserIP, (state, action) => ({
      ...state,
      ipAddress: action.payload,
    }))
    .addCase(setVersions, (state, action) => ({
      ...state,
      versions: action.payload,
    }))
    .addCase(mergeVersions, (state, action) => ({
      ...state,
      versions: { ...state.versions, ...action.payload },
    }))
    .addCase(clear, () => {
      return initState;
    });
});
